import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Order } from "../../../interfaces/order";
import { OrderForm } from "../../../interfaces/orderForm";
import { getOrderByID } from "../../../services/orderService";
import {
  getOrderFormByID,
  getOrderFormTypes,
  uploadCandidateForm,
} from "../../../services/orderFormService";
import { HttpService } from "../../../services/httpService";
import { OrderFormType } from "../../../interfaces/orderFormType";
import { getSimpleClient } from "../../../services/clientService";
import { SimpleClient } from "../../../interfaces/client";

const NotaryUpload = () => {
  const navigate = useNavigate();
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingSpinner, setLoadingSpinner] = useState(false);
  const [order, setOrder] = useState<Order | null>(null);
  const [client, setClient] = useState<SimpleClient | null>(null);
  const [orderForm, setOrderForm] = useState<OrderForm | null>(null);
  const location = useLocation();
  const { orderID, orderFormID } = location.state || {};
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [questionnaireFormType, setQuestionnaireFormType] =
    useState<OrderFormType>();

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const order = await getOrderByID(orderID);
        setOrder(order);

        const orderForm = await getOrderFormByID(orderFormID);
        setOrderForm(orderForm);

        const orderFormTypesResponse = await getOrderFormTypes();
        let transferFormType = orderFormTypesResponse?.find(
          (o) => o.orderFormTypeID === 6
        );
        setQuestionnaireFormType(transferFormType);

        const client = await getSimpleClient(order.clientID);
        setClient(client);
      } catch (error) {
        console.error("Error loading order/form:", error);
        setErrorMessage("Error loading order details.");
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [orderFormID, orderID]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const handleDownloadForm = async () => {
    if (!order || !orderForm) return;

    try {
      setLoadingSpinner(true);
      const signedUrl = await HttpService.getFile(
        "candidate-screening-questionnaires",
        orderForm.orderFormID.toString(),
        orderForm.completedFormUrl
      );
      if (signedUrl) {
        const a = document.createElement("a");
        a.href = signedUrl;
        a.download = "Completed_Questionnaire.pdf";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Error downloading questionnaire:", error);
      setErrorMessage("Unable to download the form.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  const handleFormUpload = () => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = ".pdf";
    fileInput.onchange = (event: any) => {
      const file = event.target.files?.[0];
      if (!file) return;
      setUploadedFile(file);
      const previewUrl = URL.createObjectURL(file);
      setPreviewUrl(previewUrl);
      setSuccessMessage("Form ready for submission.");
    };
    fileInput.click();
  };

  const handleSave = async () => {
    if (!order || !questionnaireFormType || !uploadedFile) {
      setErrorMessage("Please upload the form before submitting.");
      return;
    }

    try {
      setLoadingSpinner(true);
      const response = await uploadCandidateForm(
        order,
        questionnaireFormType,
        uploadedFile
      );
      if (response) {
        navigate("/candidate/needs-review", {
          state: { successMessage: "Form successfully submitted." },
        });
      } else {
        setErrorMessage("Upload failed.");
      }
    } catch (error) {
      console.error("Error uploading form on submit:", error);
      setErrorMessage("An error occurred during upload.");
    } finally {
      setLoadingSpinner(false);
    }
  };

  return (
    <div className="content-container">
      <div className="form-header-container">
        <h1>
          {order?.candidateFirstName} {order?.candidateLastName}
        </h1>
      </div>

      {loading ? (
        <div className="loading-divider">
          <div className="loading-bar"></div>
        </div>
      ) : (
        <>
          <div className="divider"></div>
          <div className="text-form-header">NOTARY REQUIREMENT</div>

          <div className="data-container">
            <div className="text-sub-header">
              Submit Notarized Questionnaire
            </div>
            <div className="text-body">
              Download your completed questionnaire and print it. After
              notarization, upload the notarized version. Please send the
              original notarized form to {client?.diocese}. Once submitted, you
              may proceed with the final step of the background check process.
            </div>

            <div className="action-buttons">
              <button className="oxbtn btn-grey" onClick={handleDownloadForm}>
                Download Questionnaire
              </button>
              <button className="oxbtn btn-grey" onClick={handleFormUpload}>
                Upload Notarized Form
              </button>
              {previewUrl && (
                <a
                  href={previewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="link"
                >
                  View Uploaded Form
                  <i className="btn-icon bi bi-box-arrow-up-right"></i>
                </a>
              )}
            </div>
          </div>

          <div className="btn-center">
            <button
              className={`oxbtn btn-green ${!previewUrl ? "disabled" : ""}`}
              onClick={handleSave}
              disabled={!previewUrl}
            >
              Submit
            </button>
          </div>
          <div>Please click submit to save your uploaded form!</div>
        </>
      )}
      <br></br>
      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}
      {errorMessage && (
        <div className="alert alert-danger" role="alert">
          {errorMessage}
        </div>
      )}
      {loadingSpinner && (
        <div className="loading-mask">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
};

export default NotaryUpload;
